import React, { useState, useMemo } from 'react';

/**
 * Parse markdown links and numbered citations, returning React elements with clickable links.
 * Handles:
 * - Inline markdown links: [text](url)
 * - Numbered citations: [1], [2], etc. (linked to sources map)
 *
 * @param {string} content - The text content to parse
 * @param {Object} sourcesMap - Map of citation numbers to URLs (e.g., { '1': 'https://...', '2': 'https://...' })
 */
function parseMarkdownContent(content, sourcesMap = {}) {
  if (!content) return null;

  // Combined regex to match:
  // 1. Markdown links: [text](url)
  // 2. Numbered citations: [1], [2], etc.
  const combinedRegex = /\[([^\]]+)\]\(([^)]+)\)|\[(\d+)\]/g;

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      // Markdown link: [text](url)
      parts.push(
        <a
          key={`link-${match.index}`}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="learn-message__link"
          onClick={(e) => e.stopPropagation()}
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      // Numbered citation: [1], [2], etc.
      const citationNum = match[3];
      const sourceUrl = sourcesMap[citationNum];

      if (sourceUrl) {
        parts.push(
          <a
            key={`cite-${match.index}`}
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="learn-message__citation"
            onClick={(e) => e.stopPropagation()}
            title={`Source ${citationNum}: ${sourceUrl}`}
          >
            [{citationNum}]
          </a>
        );
      } else {
        // No matching source, render as plain text
        parts.push(`[${citationNum}]`);
      }
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}

/**
 * Extract sources section from content if present.
 * Perplexity often includes a "Sources:" or "References:" section at the end.
 * Also builds a sourcesMap for numbered citations.
 *
 * @returns {{ mainContent: string, sources: Array, sourcesMap: Object }}
 */
function extractSources(content) {
  if (!content) return { mainContent: content, sources: [], sourcesMap: {} };

  // Look for sources section at the end (various formats Perplexity uses)
  const sourcesPatterns = [
    /\n\n(?:Sources|References|Citations):\s*\n([\s\S]+)$/i,
    /\n\n\*\*(?:Sources|References|Citations)\*\*:?\s*\n([\s\S]+)$/i,
    /\n\n---\s*\n([\s\S]*(?:https?:\/\/[^\s]+[\s\S]*)+)$/i,  // Horizontal rule followed by URLs
  ];

  for (const pattern of sourcesPatterns) {
    const match = content.match(pattern);
    if (match) {
      const mainContent = content.slice(0, match.index);
      const sourcesText = match[1];

      const sources = [];
      const sourcesMap = {};

      // Try to parse numbered sources: [1] URL or 1. URL or [1]: URL
      const numberedSourceRegex = /(?:\[(\d+)\][:.]?\s*|\b(\d+)\.\s+)(https?:\/\/[^\s]+)/g;
      let numberedMatch;

      while ((numberedMatch = numberedSourceRegex.exec(sourcesText)) !== null) {
        const num = numberedMatch[1] || numberedMatch[2];
        const url = numberedMatch[3].replace(/[.,;:]+$/, ''); // Remove trailing punctuation

        try {
          const urlObj = new URL(url);
          sources.push({
            num,
            text: urlObj.hostname.replace('www.', ''),
            url
          });
          sourcesMap[num] = url;
        } catch {
          // Invalid URL, skip
        }
      }

      // If no numbered sources found, try markdown links: [text](url)
      if (sources.length === 0) {
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let linkMatch;
        let index = 1;

        while ((linkMatch = linkRegex.exec(sourcesText)) !== null) {
          sources.push({
            num: String(index),
            text: linkMatch[1],
            url: linkMatch[2]
          });
          sourcesMap[String(index)] = linkMatch[2];
          index++;
        }
      }

      // If still no sources found, try plain URLs
      if (sources.length === 0) {
        const urlRegex = /https?:\/\/[^\s]+/g;
        let urlMatch;
        let index = 1;

        while ((urlMatch = urlRegex.exec(sourcesText)) !== null) {
          try {
            const url = urlMatch[0].replace(/[.,;:]+$/, '');
            const urlObj = new URL(url);
            sources.push({
              num: String(index),
              text: urlObj.hostname.replace('www.', ''),
              url
            });
            sourcesMap[String(index)] = url;
            index++;
          } catch {
            // Invalid URL, skip
          }
        }
      }

      return { mainContent, sources, sourcesMap };
    }
  }

  // No sources section found - check for inline numbered citations and try to extract URLs
  // Look for patterns like [1] https://... anywhere in the text
  const inlineSourceRegex = /\[(\d+)\]\s*(https?:\/\/[^\s]+)/g;
  const sourcesMap = {};
  let inlineMatch;

  while ((inlineMatch = inlineSourceRegex.exec(content)) !== null) {
    const num = inlineMatch[1];
    const url = inlineMatch[2].replace(/[.,;:]+$/, '');
    sourcesMap[num] = url;
  }

  return { mainContent: content, sources: [], sourcesMap };
}

/**
 * LearnMessage - Display a single message in the Learn chat.
 *
 * Features:
 * - User and assistant message styling
 * - Hover actions: pin (bookmark) and copy
 * - Bookmark indicator for already-bookmarked messages
 * - Clickable source links for web-searched responses
 */
export default function LearnMessage({
  message,
  onBookmark,
  isBookmarking = false
}) {
  const { role, content, bookmark_id } = message;
  const isBookmarked = !!bookmark_id;
  const [copied, setCopied] = useState(false);

  // Parse content and extract sources
  const { mainContent, sources, sourcesMap } = useMemo(
    () => extractSources(content),
    [content]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className={`learn-message learn-message--${role}`}>
      <div className="learn-message__content">
        {parseMarkdownContent(mainContent, sourcesMap)}
      </div>

      {/* Sources section for assistant messages with citations */}
      {role === 'assistant' && sources.length > 0 && (
        <div className="learn-message__sources">
          <div className="learn-message__sources-label">Sources</div>
          <div className="learn-message__sources-list">
            {sources.map((source, index) => (
              <a
                key={index}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="learn-message__source-link"
                title={source.url}
              >
                {source.text}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Hover actions for all messages */}
      <div className="learn-message__actions">
        {/* Copy button */}
        <button
          className="learn-message__action-btn"
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? '✓' : '📋'}
        </button>

        {/* Pin/bookmark button (assistant messages only) */}
        {role === 'assistant' && (
          <button
            className={`learn-message__action-btn learn-message__pin-btn ${isBookmarked ? 'learn-message__pin-btn--active' : ''}`}
            onClick={onBookmark}
            disabled={isBookmarked || isBookmarking}
            title={isBookmarked ? 'Pinned to Library' : 'Pin to Library'}
          >
            📌
          </button>
        )}
      </div>
    </div>
  );
}
